import axios from "axios";
import { importOutreachData } from "../services/outreach/firestore/import";
import {
  ReactionChannel,
  PlaylistingChannel,
  SpotifyPlaylist,
  MusicBlog,
  MusicIndustry,
} from "../types/outreach";

// Google Sheets API key
const API_KEY = "AIzaSyDEcku8FKiH_eKXtTGebEikHalfcCrNwAM";

// Function to extract URL and text from a hyperlink cell in Google Sheets
const extractHyperlink = (cell: any): { text: string; url: string } => {
  console.log("Cell value:", JSON.stringify(cell, null, 2));

  // Check if cell is an object with a hyperlink property
  if (typeof cell === "object" && cell !== null) {
    // If cell has a direct hyperlink property
    if (cell.hyperlink) {
      return {
        text: cell.text || String(cell.v || ""),
        url: cell.hyperlink || "",
      };
    }

    // If cell has a formatted value with link
    if (cell.f && typeof cell.f === "string" && cell.f.includes("HYPERLINK")) {
      // Extract URL from HYPERLINK formula
      const urlMatch = cell.f.match(/HYPERLINK\s*\(\s*"([^"]+)"/i);
      const textMatch = cell.f.match(/HYPERLINK\s*\([^,]*,\s*"([^"]+)"/i);

      return {
        text: textMatch ? textMatch[1] : String(cell.v || ""),
        url: urlMatch ? urlMatch[1] : "",
      };
    }

    // If cell has a link property
    if (cell.link) {
      return {
        text: String(cell.v || ""),
        url: cell.link || "",
      };
    }
  }

  // If it's just a string, return it as text with empty URL
  return {
    text: String(cell || ""),
    url: "",
  };
};

// Helper function to fetch data from a specific tab
const fetchTabData = async (sheetId: string, gid: string): Promise<any> => {
  try {
    const sheetUrl = `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:json&gid=${gid}`;
    const response = await axios.get(sheetUrl);

    // Parse the response (Google Sheets API returns a callback-like string)
    const data = JSON.parse(
      response.data.substring(
        response.data.indexOf("(") + 1,
        response.data.lastIndexOf(")")
      )
    );

    return data.table.rows;
  } catch (error) {
    console.error(`Error fetching tab with gid ${gid}:`, error);
    return [];
  }
};

// New function to fetch hyperlinks for a specific range using the Google Sheets API v4
const fetchHyperlinks = async (
  sheetId: string,
  range: string
): Promise<any> => {
  try {
    console.log(`Fetching hyperlinks for range ${range}...`);

    // Use the Google Sheets API v4 with the fields parameter to get hyperlinks
    // The range should be in the format "Sheet1!A1:A100"
    const apiUrl = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}?ranges=${encodeURIComponent(
      range
    )}&fields=sheets(data(rowData(values(hyperlink))))&key=${API_KEY}`;

    const response = await axios.get(apiUrl);
    console.log("Hyperlink response:", JSON.stringify(response.data, null, 2));

    return response.data;
  } catch (error) {
    console.error(`Error fetching hyperlinks for range ${range}:`, error);
    return null;
  }
};

// Function to import data from Google Sheet
export const importFromGoogleSheet = async (sheetId: string): Promise<void> => {
  try {
    // In a real application, you would use the Google Sheets API with proper authentication
    // For this demo, we'll simulate the API call with a direct access to the published sheet
    // This approach requires the sheet to be published to the web

    // Define the gid (tab ID) for each tab in the Google Sheet
    // These are the actual gids from the Google Sheet URL
    const reactionChannelsGid = "2112799511"; // Reaction Channels '23
    const playlistingChannelsGid = "540661768"; // Playlisting Channels
    const spotifyPlaylistsGid = "504363139"; // Spotify Playlists Curators
    const musicBlogsGid = "1343117572"; // Music Blogs
    const musicIndustryGid = "620066081"; // Music Industry

    // Process each tab
    const reactionChannels: Omit<ReactionChannel, "id">[] = [];
    const playlistingChannels: Omit<PlaylistingChannel, "id">[] = [];
    const spotifyPlaylists: Omit<SpotifyPlaylist, "id">[] = [];
    const musicBlogs: Omit<MusicBlog, "id">[] = [];
    const musicIndustry: Omit<MusicIndustry, "id">[] = [];

    // Fetch and process Reaction Channels tab
    console.log("Fetching Reaction Channels tab...");
    const reactionTab = await fetchTabData(sheetId, reactionChannelsGid);

    // Log the first row (header) to understand the structure
    if (reactionTab.length > 0) {
      console.log(
        "Reaction Channels header row:",
        reactionTab[0].c
          ? reactionTab[0].c.map((cell: any) => cell?.v || "").join(", ")
          : "No cells found"
      );

      // Log the first data row to understand the structure
      if (reactionTab.length > 1) {
        console.log(
          "First data row structure:",
          JSON.stringify(reactionTab[1], null, 2)
        );
      }
    }

    // Note: We no longer need to fetch hyperlinks for the channel name column
    // as we're now reading the Channel URL directly from column B

    reactionTab.forEach((row: any, index: number) => {
      // Skip header row
      if (index === 0) return;

      const cells = row.c;
      if (!cells || cells.length < 5) return;

      try {
        // Log the entire row for debugging
        console.log(
          `Processing Reaction Channel row ${index}:`,
          JSON.stringify(cells, null, 2)
        );

        // Extract channel name from the first cell (Column A)
        const channelName = String(cells[0]?.v || "");

        // Get the channel URL from the second cell (Column B)
        const channelUrl = String(cells[1]?.v || "");

        // Get subscriber count from the third cell (Column C)
        const subscriberCount = String(cells[2]?.v || "");

        // Get genre from the fourth cell (Column D)
        // Since genre is a dropdown, we need to extract the value
        const genreCell = cells[3];
        let genre = "Any";
        if (genreCell && genreCell.v) {
          genre = String(genreCell.v);
        }

        // Get email from the fifth cell (Column E)
        const email = String(cells[5]?.v || "");

        // Get social handles from the sixth cell (Column F)
        const socialHandles = String(cells[6]?.v || "");

        // Log the extracted data for debugging
        console.log("Extracted data:", {
          channelName,
          channelUrl,
          subscriberCount,
          genre,
          email,
          socialHandles,
        });

        reactionChannels.push({
          channelName,
          channelUrl,
          subscriberCount,
          genre,
          email,
          socialHandles,
        });
      } catch (error) {
        console.error(`Error processing Reaction Channel row ${index}:`, error);
      }
    });
    console.log(`Processed ${reactionChannels.length} reaction channels`);

    // Fetch and process Playlisting Channels tab
    console.log("Fetching Playlisting Channels tab...");
    const playlistingTab = await fetchTabData(sheetId, playlistingChannelsGid);

    // Log the first row (header) to understand the structure
    if (playlistingTab.length > 0) {
      console.log(
        "Playlisting Channels header row:",
        playlistingTab[0].c
          ? playlistingTab[0].c.map((cell: any) => cell?.v || "").join(", ")
          : "No cells found"
      );
    }

    playlistingTab.forEach((row: any, index: number) => {
      // Skip header row
      if (index === 0) return;

      const cells = row.c;
      if (!cells || cells.length < 4) return;

      try {
        // Log the entire row for debugging
        console.log(
          `Processing Playlisting Channel row ${index}:`,
          JSON.stringify(cells, null, 2)
        );

        // Extract channel name from the first cell (Column A)
        const channelName = String(cells[0]?.v || "");

        // Get the channel URL from the second cell (Column B)
        const channelUrl = String(cells[1]?.v || "");

        // Get subscriber count from the third cell (Column C)
        const subscriberCount = String(cells[2]?.v || "");

        // Get genre from the fourth cell (Column D)
        // Since genre is a dropdown, we need to extract the value
        const genreCell = cells[3];
        let genre = "Any";
        if (genreCell && genreCell.v) {
          genre = String(genreCell.v);
        }

        // Get email from the fifth cell (Column E)
        const email = String(cells[5]?.v || "");

        // Log the extracted data for debugging
        console.log("Extracted Playlisting Channel data:", {
          channelName,
          channelUrl,
          subscriberCount,
          genre,
          email,
        });

        playlistingChannels.push({
          channelName,
          channelUrl,
          subscriberCount,
          genre,
          email,
        });
      } catch (error) {
        console.error(
          `Error processing Playlisting Channel row ${index}:`,
          error
        );
      }
    });
    console.log(`Processed ${playlistingChannels.length} playlisting channels`);

    // Fetch and process Spotify Playlists tab
    console.log("Fetching Spotify Playlists Curators tab...");
    const spotifyTab = await fetchTabData(sheetId, spotifyPlaylistsGid);
    console.log(
      `Received ${spotifyTab.length} rows from Spotify Playlists Curators tab`
    );

    // Log the first row (header) to understand the structure
    if (spotifyTab.length > 0) {
      console.log(
        "Spotify Playlists Curators header row:",
        spotifyTab[0].c
          ? spotifyTab[0].c.map((cell: any) => cell?.v || "").join(", ")
          : "No cells found"
      );

      // Log the first data row to understand the structure
      if (spotifyTab.length > 1) {
        console.log(
          "First Spotify Playlist data row structure:",
          JSON.stringify(spotifyTab[1], null, 2)
        );
      }
    }

    spotifyTab.forEach((row: any, index: number) => {
      // Skip header row
      if (index === 0) {
        console.log("Skipping header row");
        return;
      }

      const cells = row.c;
      if (!cells) {
        console.log(`Row ${index} has no cells`);
        return;
      }

      if (cells.length < 6) {
        console.log(
          `Row ${index} has only ${cells.length} cells, need at least 7`
        );
        return;
      }

      try {
        // Log the entire row for debugging
        console.log(
          `Processing Spotify Playlist row ${index}:`,
          JSON.stringify(cells, null, 2)
        );

        // Extract playlist name and URL from the first cell
        const playlistCell = cells[0];
        let playlistName = String(cells[1]?.v || "");
        let playlistUrl = String(cells[2]?.v || "");

        if (playlistCell) {
          // If the cell has a formatted value (f) that includes HYPERLINK
          if (playlistCell.f && playlistCell.f.includes("HYPERLINK")) {
            console.log("got HYPERLINK in spotify!!!!");
            // Extract URL from HYPERLINK formula
            const urlMatch = playlistCell.f.match(
              /HYPERLINK\s*\(\s*"([^"]+)"/i
            );
            playlistUrl = urlMatch ? urlMatch[1] : "";

            // Extract text from HYPERLINK formula
            const textMatch = playlistCell.f.match(
              /HYPERLINK\s*\([^,]*,\s*"([^"]+)"/i
            );
            playlistName = textMatch
              ? textMatch[1]
              : String(playlistCell.v || "");
          } else {
            // If it's just a regular cell, use the value as the playlist name
            playlistName = String(cells[1]?.v || "");
          }
        }

        // Get curator social handle from the second cell
        const curatorSocialHandle = String(cells[3]?.v || "");

        // Get source from the third cell
        const source = String(cells[4]?.v || "");

        // Get genre from the fourth cell
        // Since genre is a dropdown, we need to extract the value
        const genreCell = cells[5];
        let genre = "Any";
        if (genreCell && genreCell.v) {
          genre = String(genreCell.v);
        }

        // Get curator email from the fifth cell
        const curatorEmail = String(cells[6]?.v || "");

        // Get playlist likes from the sixth cell
        const playlistLikes = Number(cells[7]?.v || 0);

        // Get playlist followers from the seventh cell
        const playlistFollowers = Number(cells[8]?.v || 0);

        // Log the extracted data for debugging
        console.log("Extracted Spotify Playlist data:", {
          playlistName,
          playlistUrl,
          curatorSocialHandle,
          source,
          genre,
          curatorEmail,
          playlistLikes,
          playlistFollowers,
        });

        spotifyPlaylists.push({
          playlistName,
          playlistUrl,
          curatorSocialHandle,
          source,
          genre,
          curatorEmail,
          playlistLikes,
          playlistFollowers,
        });
      } catch (error) {
        console.error(`Error processing Spotify Playlist row ${index}:`, error);
      }
    });
    console.log(`Processed ${spotifyPlaylists.length} spotify playlists`);

    // Fetch and process Music Blogs tab
    console.log("Fetching Music Blogs tab...");
    const blogsTab = await fetchTabData(sheetId, musicBlogsGid);

    // Log the first row (header) to understand the structure
    if (blogsTab.length > 0) {
      console.log(
        "Music Blogs header row:",
        blogsTab[0].c
          ? blogsTab[0].c.map((cell: any) => cell?.v || "").join(", ")
          : "No cells found"
      );

      // Log the first data row to understand the structure
      if (blogsTab.length > 1) {
        console.log(
          "First Music Blog data row structure:",
          JSON.stringify(blogsTab[1], null, 2)
        );
      }
    }

    blogsTab.forEach((row: any, index: number) => {
      // Skip header row
      if (index === 0) return;

      const cells = row.c;
      if (!cells || cells.length < 4) return;

      try {
        // Log the entire row for debugging
        console.log(
          `Processing Music Blog row ${index}:`,
          JSON.stringify(cells, null, 2)
        );

        // Get blog name from the first cell
        const blogName = String(cells[0]?.v || "");

        // Get contact from the second cell
        const contact = String(cells[1]?.v || "");

        // Extract blog URL from the third cell
        const blogUrlCell = cells[2];
        let blogUrl = "";

        if (blogUrlCell) {
          // If the cell has a formatted value (f) that includes HYPERLINK
          if (blogUrlCell.f && blogUrlCell.f.includes("HYPERLINK")) {
            // Extract URL from HYPERLINK formula
            const urlMatch = blogUrlCell.f.match(/HYPERLINK\s*\(\s*"([^"]+)"/i);
            blogUrl = urlMatch ? urlMatch[1] : "";
          } else if (
            typeof blogUrlCell.v === "string" &&
            blogUrlCell.v.startsWith("http")
          ) {
            // If it's a plain URL
            blogUrl = blogUrlCell.v;
          }
        }

        // Get monthly visits from the fourth cell
        const monthlyVisits = Number(cells[3]?.v || 0);

        // Log the extracted data for debugging
        console.log("Extracted Music Blog data:", {
          blogName,
          contact,
          blogUrl,
          genre: "Any", // Default genre
          monthlyVisits,
        });

        musicBlogs.push({
          blogName,
          contact,
          blogUrl,
          genre: "Any", // Default genre since it's not in the sheet
          monthlyVisits,
        });
      } catch (error) {
        console.error(`Error processing Music Blog row ${index}:`, error);
      }
    });
    console.log(`Processed ${musicBlogs.length} music blogs`);

    // Fetch and process Music Industry tab
    console.log("Fetching Music Industry tab...");
    const industryTab = await fetchTabData(sheetId, musicIndustryGid);

    // Log the first row (header) to understand the structure
    if (industryTab.length > 0) {
      console.log(
        "Music Industry header row:",
        industryTab[0].c
          ? industryTab[0].c.map((cell: any) => cell?.v || "").join(", ")
          : "No cells found"
      );

      // Log the first data row to understand the structure
      if (industryTab.length > 1) {
        console.log(
          "First Music Industry data row structure:",
          JSON.stringify(industryTab[1], null, 2)
        );
      }
    }

    industryTab.forEach((row: any, index: number) => {
      // Skip header row
      if (index === 0) return;

      const cells = row.c;
      if (!cells || cells.length < 5) return;

      try {
        // Log the entire row for debugging
        console.log(
          `Processing Music Industry row ${index}:`,
          JSON.stringify(cells, null, 2)
        );

        // Get company from the first cell
        const company = String(cells[0]?.v || "");

        // Get contact from the second cell
        const contact = String(cells[1]?.v || "");

        // Get email from the third cell
        const email = String(cells[2]?.v || "");

        // Extract website URL from the fourth cell
        const siteUrlCell = cells[3];
        let siteUrl = "";

        if (siteUrlCell) {
          // If the cell has a formatted value (f) that includes HYPERLINK
          if (siteUrlCell.f && siteUrlCell.f.includes("HYPERLINK")) {
            // Extract URL from HYPERLINK formula
            const urlMatch = siteUrlCell.f.match(/HYPERLINK\s*\(\s*"([^"]+)"/i);
            siteUrl = urlMatch ? urlMatch[1] : "";
          } else if (
            typeof siteUrlCell.v === "string" &&
            siteUrlCell.v.startsWith("http")
          ) {
            // If it's a plain URL
            siteUrl = siteUrlCell.v;
          }
        }

        // Get notes from the fifth cell
        const notes = String(cells[4]?.v || "");

        // Log the extracted data for debugging
        console.log("Extracted Music Industry data:", {
          company,
          contact,
          email,
          siteUrl,
          genre: "Any", // Default genre
          notes,
        });

        musicIndustry.push({
          company,
          contact,
          email,
          siteUrl,
          genre: "Any", // Default genre since it's not in the sheet
          notes,
        });
      } catch (error) {
        console.error(`Error processing Music Industry row ${index}:`, error);
      }
    });
    console.log(`Processed ${musicIndustry.length} music industry contacts`);

    // Import all data to Firebase
    await importOutreachData(
      reactionChannels,
      playlistingChannels,
      spotifyPlaylists,
      musicBlogs,
      musicIndustry
    );

    console.log("Google Sheet data imported successfully");
  } catch (error) {
    console.error("Error importing Google Sheet data:", error);
    throw error;
  }
};

// Function to import the specific Google Sheet from the task
export const importDistroNationSheet = async (): Promise<void> => {
  const sheetId = "1nkGA76CrtTpykxLit9e_8WCN33-rwsuf65t9Ces_P1k";
  await importFromGoogleSheet(sheetId);
  console.log("DistroNation sheet import completed");
};
